import type { DsdFileItem } from "@/apis/dsd-dsd-compare/types";
import { DsdFile, DsdFileStatus } from "@/types/dsd";
import { DeletedUserAvatar } from "@/components/common/deleted-user-avatar";
import { TypeChip, type ReportType } from "@/components/common/report-chip";
import { useUserNames } from "@/hooks/queries/use-user-names";
import { cn } from "@/lib/utils";
import queries from "@/queries";
import { Avatar } from "@/toggle-ui-kit/avatar";
import { Button } from "@/toggle-ui-kit/button";
import { getFormattedTimeWithDate } from "@/utils/time";
import { getAuthorDisplayName, isDeletedUser } from "@/utils/user-display-name";
import { useQuery } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronRight,
  FolderClosed,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";

// ============================================
// Types
// ============================================

export type DsdSelectModalType = "base" | "compare";

export interface DsdSelectModalProps {
  type: DsdSelectModalType;
  selectedFile?: DsdFile | null;
  filterByFile?: DsdFile | null;
  onSelect?: (file: DsdFile) => void;
  onClose: () => void;
}

// ============================================
// Constants
// ============================================

const MODAL_CONFIG = {
  base: { step: 1, title: "DSD 보고서" },
  compare: { step: 2, title: "DSD 보고서" },
} as const;

const PERIOD_ORDER: Record<string, number> = {
  사업: 4,
  "3분기": 3,
  반기: 2,
  "1분기": 1,
};

const PERIOD_MAP: Record<string, string> = {
  사업: "Q4",
  "1분기": "Q1",
  반기: "Q2",
  "3분기": "Q3",
};

const REPORT_TYPE_MAP: Record<string, string> = {
  연결: "CONSOLIDATED",
  별도: "SEPARATE",
};

// ============================================
// Helpers
// ============================================

const toDsdFile = (item: DsdFileItem): DsdFile => {
  const yearMatch = item.year.match(/(\d{4})/);
  const reportYear = yearMatch ? parseInt(yearMatch[1], 10) : null;
  return {
    id: item.fileId,
    uploadedFileId: item.fileId,
    name: item.filename,
    displayName: item.filename,
    creator: item.uploaderEmail,
    revision: 0,
    prevWorkbookId: null,
    versionNo: 1,
    reportQuarter: PERIOD_MAP[item.period] ?? null,
    reportYear,
    financialStatementType: REPORT_TYPE_MAP[item.reportType] ?? item.reportType,
    companyCode: null,
    createdAt: item.createdAt,
    updatedAt: item.createdAt,
    label: item.filename,
    status: DsdFileStatus.DSD_ANALYZED,
  };
};

const formatDate = (dateStr: string) =>
  getFormattedTimeWithDate(dateStr).replace(/\s/g, "").replace(/\.$/, "");

// ============================================
// Round Checkbox
// ============================================

function RoundCheckbox({ checked }: { checked: boolean }) {
  return (
    <div
      className={cn(
        "flex size-4 items-center justify-center rounded-full bg-primary-base",
        !checked && "opacity-[0.24]",
      )}
    >
      <Check className="size-2 text-static-white" strokeWidth={3} />
    </div>
  );
}

// ============================================
// DsdListItem
// ============================================

export interface DsdListItemProps {
  selected?: boolean;
  disabled?: boolean;
  reportType: ReportType;
  name: string;
  authorName: string;
  authorEmail: string;
  isDeletedAuthor?: boolean;
  date: string;
  onClick?: () => void;
}

export function DsdListItem({
  selected = false,
  disabled = false,
  reportType,
  name,
  authorName,
  authorEmail,
  isDeletedAuthor = false,
  date,
  onClick,
}: DsdListItemProps) {
  return (
    <button
      className={cn(
        "group relative flex w-full items-center bg-background-base pl-3",
        disabled ? "cursor-default opacity-[0.4]" : "cursor-pointer",
      )}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      {/* Checkbox */}
      <div className="flex shrink-0 items-center self-stretch px-3 py-3">
        <RoundCheckbox checked={selected} />
      </div>

      {/* Report Type */}
      <div className="flex h-[52px] shrink-0 items-center px-3 py-3">
        <TypeChip type={reportType} />
      </div>

      {/* Report Name */}
      <div className="flex min-w-0 flex-1 items-center self-stretch px-3 py-3">
        <p className="body-2-normal-medium truncate text-label-normal">
          {name}
        </p>
      </div>

      {/* Author */}
      <div className="flex h-[52px] w-[128px] shrink-0 items-center gap-2 px-3 py-3">
        {isDeletedAuthor ? (
          <DeletedUserAvatar size="20" />
        ) : (
          <Avatar
            size="20"
            type="label"
            displayName={authorName}
            email={authorEmail}
          />
        )}
        <p className="body-2-normal-medium min-w-0 flex-1 truncate text-label-normal">
          {authorName}
        </p>
      </div>

      {/* Date */}
      <div className="flex w-[128px] shrink-0 items-center self-stretch px-3 py-3">
        <p className="body-2-normal-medium whitespace-nowrap text-label-normal">
          {date}
        </p>
      </div>

      {/* Interaction Overlay */}
      {!disabled && (
        <div
          className={cn(
            "pointer-events-none absolute inset-0",
            selected
              ? "bg-blue-500/[.09]"
              : "bg-neutral-800/[.045] opacity-0 group-hover:opacity-100",
          )}
        />
      )}
    </button>
  );
}

// ============================================
// DsdAccordionGroup
// ============================================

interface DsdAccordionGroupProps {
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export function DsdAccordionGroup({
  label,
  defaultOpen = true,
  children,
}: DsdAccordionGroupProps) {
  const [open, setOpen] = useState(defaultOpen);

  if (!open) {
    return (
      <button
        className="flex w-full cursor-pointer items-center gap-1 border-b border-line-normal bg-white p-3"
        onClick={() => setOpen(true)}
        type="button"
      >
        <ChevronRight className="size-4 shrink-0 text-label-neutral" />
        <p className="label-2-medium min-w-0 flex-1 truncate text-left text-label-neutral">
          {label}
        </p>
      </button>
    );
  }

  return (
    <div className="flex w-full flex-col overflow-clip border-b border-line-normal bg-white">
      <button
        className="flex w-full cursor-pointer items-center gap-1 p-3"
        onClick={() => setOpen(false)}
        type="button"
      >
        <ChevronDown className="size-4 shrink-0 text-label-neutral" />
        <p className="label-2-medium min-w-0 flex-1 truncate text-left text-label-neutral">
          {label}
        </p>
      </button>
      {children}
    </div>
  );
}

// ============================================
// DsdSelectModal2
// ============================================

export function DsdSelectModal({
  type,
  selectedFile,
  filterByFile,
  onSelect,
  onClose,
}: DsdSelectModalProps) {
  const config = MODAL_CONFIG[type];

  const { data, isLoading } = useQuery({
    ...queries.dsdDsdCompare()._ctx.files(),
  });
  const files = data?.files;

  const creatorEmails = useMemo(
    () => files?.map((f) => f.uploaderEmail) ?? [],
    [files],
  );
  const { data: userNamesMap } = useUserNames(creatorEmails);

  const [selectedId, setSelectedId] = useState<string | null>(
    selectedFile?.id ?? null,
  );

  // year → period → items 2단 그룹핑
  const grouped = useMemo(() => {
    const filtered =
      files?.filter((f) => {
        if (!filterByFile) return true;
        return f.fileId !== filterByFile.id;
      }) ?? [];

    const byYear = new Map<string, Map<string, DsdFileItem[]>>();
    for (const file of filtered) {
      if (!byYear.has(file.year)) byYear.set(file.year, new Map());
      const yearMap = byYear.get(file.year)!;
      if (!yearMap.has(file.period)) yearMap.set(file.period, []);
      yearMap.get(file.period)!.push(file);
    }

    return Array.from(byYear.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([year, periodsMap]) => ({
        year,
        periods: Array.from(periodsMap.entries())
          .sort(([a], [b]) => (PERIOD_ORDER[a] ?? 0) - (PERIOD_ORDER[b] ?? 0))
          .map(([period, items]) => ({
            period,
            items: items.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            ),
          })),
      }));
  }, [files, filterByFile]);

  const selectedFileData = files?.find((f) => f.fileId === selectedId) ?? null;

  const handleItemClick = (fileId: string) => {
    setSelectedId((prev) => (prev === fileId ? null : fileId));
  };

  const handleConfirm = () => {
    if (selectedFileData) {
      onSelect?.(toDsdFile(selectedFileData));
      onClose();
    }
  };

  return (
    <div className="flex h-[800px] w-[1024px] max-h-[80vh] flex-col overflow-hidden rounded-2xl bg-white shadow-[0px_4px_24px_4px_rgba(0,0,0,0.08),0px_25px_50px_-12px_rgba(0,0,0,0.25)]">
      {/* Header */}
      <div className="relative flex flex-col justify-center border-b border-line-normal px-6 py-5">
        <div className="flex items-center gap-2">
          <div className="flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-base px-1.5">
            <span className="caption-2-medium text-center text-white whitespace-nowrap">
              {config.step}
            </span>
          </div>
          <h2 className="heading-2-semibold text-label-normal">
            {config.title}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="absolute right-3 top-1/2 -translate-y-1/2 flex size-8 items-center justify-center rounded-md transition-colors hover:bg-blue-500/[.06] active:bg-blue-500/[.12]"
          aria-label="닫기"
          type="button"
        >
          <X className="size-[18px] text-label-normal" />
        </button>
      </div>

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-white">
        {isLoading ? (
          <div className="flex flex-col gap-2 p-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-[52px] w-full animate-pulse rounded bg-neutral-100"
              />
            ))}
          </div>
        ) : grouped.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <p className="body-1-normal-medium text-label-alternative">
              업로드된 DSD 보고서가 없습니다.
            </p>
          </div>
        ) : (
          grouped.map(({ year, periods }, yearIndex) => (
            <div key={year} className="flex flex-col pb-4">
              {/* Year Header */}
              <div className="flex w-full items-center gap-1 border-b border-line-normal p-3">
                <FolderClosed className="size-4 shrink-0 text-label-alternative" />
                <span className="caption-1-semibold flex-1 truncate text-label-alternative">
                  {year}
                </span>
              </div>
              {/* Period Accordions */}
              {periods.map(({ period, items }, periodIndex) => (
                <DsdAccordionGroup
                  key={period}
                  label={period}
                  defaultOpen={
                    yearIndex === 0 && periodIndex === periods.length - 1
                  }
                >
                  {items.map((file) => (
                    <DsdListItem
                      key={file.fileId}
                      selected={selectedId === file.fileId}
                      reportType={
                        (REPORT_TYPE_MAP[file.reportType] as ReportType) ??
                        (file.reportType as ReportType)
                      }
                      name={file.filename}
                      authorName={getAuthorDisplayName(
                        userNamesMap,
                        file.uploaderEmail,
                      )}
                      authorEmail={file.uploaderEmail}
                      isDeletedAuthor={isDeletedUser(
                        userNamesMap,
                        file.uploaderEmail,
                      )}
                      date={formatDate(file.createdAt)}
                      onClick={() => handleItemClick(file.fileId)}
                    />
                  ))}
                </DsdAccordionGroup>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 bg-white px-6 py-5">
        <Button
          variant="outlined"
          color="secondary"
          size={40}
          onClick={onClose}
        >
          취소
        </Button>
        <Button
          variant="solid"
          color="secondary"
          size={40}
          disabled={!selectedId}
          className="disabled:opacity-[0.16]"
          onClick={handleConfirm}
        >
          확인
        </Button>
      </div>
    </div>
  );
}
